/**
 * A trade-in is two things at once.
 *
 * It is a line on the deal — an allowance that reduces the taxable amount,
 * and a payoff added after tax — and it is a car that is now on the lot. The
 * app used to record only the first, so a traded car had to be typed again as
 * a vehicle or, in practice, went unrecorded: sitting on the lot with no
 * record of where it came from or what it cost.
 *
 * This writes both, in one place, so they cannot disagree.
 */

import { supabase } from "../../lib/supabase";
import { validateVin, validateLegacyVin } from "../../lib/vin";

export interface TradeInput {
  vin: string;
  vinNonStandard: boolean;
  year: number;
  make: string;
  model: string;
  trim: string;
  color: string;
  odometer: number;
  /** What was allowed for it. This is the cost of the car to the dealer. */
  allowanceCents: number;
  payoffCents: number;
  lienholderName: string;
  titleState: string;
}

export class TradeError extends Error {
  constructor(message: string, readonly field?: string) {
    super(message);
    this.name = "TradeError";
  }
}

/** Everything the deal screen should refuse before it writes anything. */
export function checkTrade(t: TradeInput): string | null {
  const vin = t.vinNonStandard ? validateLegacyVin(t.vin) : validateVin(t.vin);
  if (!vin.ok) return vin.message;
  if (!t.year) return "Enter the trade's year.";
  if (!t.make.trim()) return "Enter the trade's make.";
  if (!t.model.trim()) return "Enter the trade's model.";
  if (!t.odometer) return "Enter the trade's mileage.";
  if (!t.color.trim()) return "Enter the trade's colour.";
  if (t.allowanceCents <= 0) return "Enter what you allowed for the trade.";
  return null;
}

/**
 * Put the traded car on the lot and record the trade against the deal.
 *
 * Order matters. The vehicle is created first: if that fails — most often a
 * VIN already on the lot — nothing else has happened and the deal is
 * untouched. The trade row is written second and points at the vehicle, so a
 * trade can never reference a car that does not exist.
 */
export async function recordTrade(
  dealId: string,
  t: TradeInput,
  tradedBy: string,
  today = new Date().toISOString().slice(0, 10),
): Promise<{ vehicleId: string; stockNo: string }> {
  const problem = checkTrade(t);
  if (problem) throw new TradeError(problem);

  const { data: vehicle, error: vehicleError } = await supabase
    .from("vehicles")
    .insert({
      vin: t.vin.toUpperCase(),
      vin_non_standard: t.vinNonStandard,
      year: t.year,
      make: t.make.trim(),
      model: t.model.trim(),
      trim_level: t.trim.trim() || null,
      exterior_color: t.color.trim(),
      odometer: t.odometer,
      purchase_date: today,
      purchased_from: tradedBy,
      source_type: "Trade-In",
      /* What the car cost: the allowance credited against the new vehicle.
         The payoff is money sent to a lienholder on the customer's behalf —
         part of the deal, not the cost of this car. */
      purchase_price_cents: t.allowanceCents,
      /* Not priced yet. It needs looking over before it is advertised, and a
         guess here would flow into the gross on every report. */
      asking_price_cents: 0,
      status: "purchased",
      title_status: "not_received",
    })
    .select("id, stock_no")
    .single();

  if (vehicleError || !vehicle) {
    const msg = vehicleError?.message ?? "no row returned";
    if (/vehicles_active_vin_uniq|duplicate key/i.test(msg)) {
      throw new TradeError(
        "That VIN is already on the lot. Check whether this car is "
        + "already in inventory before taking it in again.", "vin");
    }
    throw new TradeError(`Could not add the trade to inventory: ${msg}`);
  }

  const { error: tradeError } = await supabase.from("trade_ins").insert({
    deal_id: dealId,
    vehicle_id: vehicle.id,
    vin: t.vin.toUpperCase(),
    vin_non_standard: t.vinNonStandard,
    year: t.year,
    make: t.make.trim(),
    model: t.model.trim(),
    color: t.color.trim() || null,
    odometer: t.odometer,
    title_state: t.titleState || null,
    lienholder_name: t.lienholderName.trim() || null,
  });

  if (tradeError) {
    /* The vehicle exists but nothing points at it. Remove it rather than
       leave a car on the lot that no deal accounts for. */
    await supabase.from("vehicles").delete().eq("id", vehicle.id);
    throw new TradeError(`Could not record the trade: ${tradeError.message}`);
  }

  return { vehicleId: vehicle.id as string, stockNo: vehicle.stock_no as string };
}
